import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import UserService from '../../services/user.service';

const UserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: '',
    confirmed: true,
    blocked: false,
    preferredLanguage: 'en',
  });

  useEffect(() => {
    fetchRoles();
    if (isEdit && id) {
      fetchUser(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchRoles = async () => {
    try {
      const response = await UserService.getRoles();
      setRoles(response.data?.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchUser = async (userId: number) => {
    try {
      const response = await UserService.getOne(userId);
      const user = response.data;
      if (user) {
        setFormData({
          username: user.username,
          email: user.email,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          role: user.role?.id || '',
          confirmed: user.confirmed ?? true,
          blocked: user.blocked ?? false,
          preferredLanguage: user.preferredLanguage || 'en',
        });
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData: any = {
        username: formData.username,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role ? parseInt(formData.role) : undefined,
        confirmed: formData.confirmed,
        blocked: formData.blocked,
        preferredLanguage: formData.preferredLanguage,
      };

      // Only include password if it's provided
      if (!isEdit || formData.password) {
        if (!formData.password) {
          setError('Password is required for new users');
          setLoading(false);
          return;
        }
        submitData.password = formData.password;
      }

      if (isEdit && id) {
        await UserService.update(parseInt(id), submitData);
      } else {
        await UserService.create(submitData);
      }
      navigate('/users');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit User' : 'Create User'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Typography variant="h6" color="primary">
              Account Information
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={isEdit}
                helperText={isEdit ? 'Username cannot be changed' : 'Must be unique'}
              />
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </Stack>

            <TextField
              fullWidth
              label={isEdit ? 'New Password (leave blank to keep current)' : 'Password'}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required={!isEdit}
              helperText={
                isEdit
                  ? 'Leave blank to keep the current password'
                  : 'Minimum 6 characters'
              }
              inputProps={{ minLength: 6 }}
            />

            <Divider />

            <Typography variant="h6" color="primary">
              Role & Permissions
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                required
                label="Role"
                name="role"
                value={formData.role}
                onChange={handleChange}
              >
                <MenuItem value="">Select Role</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name} - {role.description}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                select
                label="Preferred Language"
                name="preferredLanguage"
                value={formData.preferredLanguage}
                onChange={handleChange}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="ar">العربية (Arabic)</MenuItem>
                <MenuItem value="fr">Français (French)</MenuItem>
                <MenuItem value="es">Español (Spanish)</MenuItem>
              </TextField>
            </Stack>

            <Divider />

            <Typography variant="h6" color="primary">
              Account Status
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.confirmed}
                    onChange={handleChange}
                    name="confirmed"
                  />
                }
                label="Email Confirmed"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.blocked}
                    onChange={handleChange}
                    name="blocked"
                    color="error"
                  />
                }
                label="Block User"
              />
            </Stack>

            <Alert severity="info">
              <strong>Note:</strong> Blocked users cannot log in. Unconfirmed users may have
              limited access depending on your configuration.
            </Alert>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/users')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default UserForm;

